import {Injectable} from '@angular/core';
import {SegmentsListFilter, SegmentsListFilterPage} from './segments.state';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Segment} from './segment.model';
import {Observable} from 'rxjs';
import {TargetAccount} from '../target/target.model';
import {map} from 'rxjs/operators';
import {ApiVersion} from '../app.types';
import {Page} from '../components/common-interfaces';
import {
  SegmentDeletionValidationServerResponse
} from './list/bulk-delete-segments-dialog/bulk-delete-segments-dialog.component';

export const apiVersionHeader = (apiVersion: ApiVersion) => {
  return {headers: new HttpHeaders({Accept: apiVersion, 'Content-Type': apiVersion})};
};

@Injectable({
  providedIn: 'root'
})
export class SegmentsClientService {

  constructor(private httpClient: HttpClient) {
  }

  getSegmentsByFilter(filter: SegmentsListFilter, page?: SegmentsListFilterPage,
                      apiVersion: ApiVersion = ApiVersion.V1): Observable<Page<Segment>> {
    let params = new HttpParams();
    if (page?.page) {
      params = params.set('page', page.page);
    }
    if (page?.size) {
      params = params.set('size', page.size);
    }
    if (page?.sort?.sortField) {
      params = params.set('sortField', page.sort.sortField);
    }
    if (page?.sort?.direction) {
      params = params.set('direction', page.sort.direction);
    }
    return this.httpClient.post<Page<Segment>>(
      `${environment.apiSegments}/filtered-segments`,
      filter,
      {...apiVersionHeader(apiVersion), params});
  }

  getSharedAccountsWithSegment(id: number): Observable<TargetAccount[]> {
    const params = new HttpParams().set('segmentId', id);
    return this.httpClient.get<TargetAccount[]>(`${environment.apiSegments}/accounts`, {params})
      .pipe(map(value => value
        .map(target => ({
          ...target,
          accounts: target.accounts
            .map(acc => ({
              ...acc,
              segmentId: acc.firstShare?.segmentId,
              ttdCategoryId: acc.firstShare?.ttdCategoryId,
              ttdPricing: acc.firstShare?.ttdPricing,
              ttdCompliance: acc.firstShare?.ttdCompliance,
              ttdDisplayName: acc.firstShare?.ttdDisplayName,
            }))
        }))));
  }

  editSegment(segment: Segment, apiVersion: ApiVersion = ApiVersion.V1): Observable<Segment> {
    return this.httpClient.put<Segment>(`${environment.apiSegments}/segments/${segment.id}`,
      segment,
      apiVersionHeader(apiVersion));
  }

  deleteSegment(segment: Segment): Observable<void> {
    return this.httpClient.delete<void>(`${environment.apiSegments}/segments/${segment.id}`);
  }

  deleteSegments(segmentIds: string[]): Observable<void> {
    return this.httpClient.delete<void>(`${environment.apiSegments}/segments?segmentIds=${segmentIds.join(',')}`);
  }

  validateSegmentsDeletion(segmentIds: number[]): Observable<SegmentDeletionValidationServerResponse> {
    return this.httpClient
      .get<SegmentDeletionValidationServerResponse>
      (`${environment.apiSegments}/segments/deletion/validate?segmentIds=${segmentIds.join(',')}`);
  }

  attachSegmentsToTaxonomy(segmentIds: number[], taxonomyId: number): Observable<string> {
    return this.httpClient.put<string>(`${environment.apiSegments}/segments/bulk-edit`, {
      segmentIds,
      taxonomy: taxonomyId
    }, apiVersionHeader(ApiVersion.V2));
  }

  getCategories(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${environment.apiSegments}/segment-category`);
  }

  getBrands(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${environment.apiSegments}/segment-brand`);
  }

  getSegmentMainTypes(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${environment.apiSegments}/segment-main-types`);
  }

  getSegmentSubTypes(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${environment.apiSegments}/segment-sub-types`);
  }

  getSegmentAudienceTypes(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${environment.apiSegments}/segment-audience-types`);
  }
}
